/**
 * Google Analytics 4 Event Tracking
 */

// Amplitude import removed

declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string | Date,
      config?: Record<string, unknown>
    ) => void;
  }
}

// Env-driven para que un fork de otra marca no herede la propiedad GA4 de ana.
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID || "G-QDGH3RF9KY";
// Amplitude constants removed

/**
 * Track social media link clicks
 */
export function trackSocialClick(platform: string, linkUrl: string) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "social_click", {
    event_category: "Social Media",
    event_label: platform,
    link_url: linkUrl,
    platform: platform.toLowerCase(),
  });

  // Amplitude tracking completely removed
}

/**
 * Track photo interactions (clicks, views)
 */
export function trackPhotoInteraction(
  action: "click" | "view",
  photoId: string | number,
  photoTitle?: string,
  pagePath?: string
) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "photo_interaction", {
    event_category: "Content",
    event_label: photoTitle || `Photo ${photoId}`,
    action: action,
    photo_id: String(photoId),
    page_path: pagePath || window.location.pathname,
  });

  // Amplitude tracking removed
  // eslint-disable-next-line no-constant-condition
  if (false) {
    try {
      false("Photo Interaction", {
        action,
        photo_id: String(photoId),
        photo_title: photoTitle || `Photo ${photoId}`,
        page_path: pagePath || window.location.pathname,
        event_category: "Content",
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        // Amplitude warning removed
      }
    }
  }
}

/** GA4 mirror for PostHog `base_template_ui_click` */
export function trackBaseTemplateUIGA(params: {
  templateName: string;
  elementId: string;
  label: string;
  surface: string;
  href?: string;
  extra?: Record<string, unknown>;
}) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "base_template_ui_click", {
    event_category: "BaseTemplate",
    template_name: params.templateName,
    event_label: params.label,
    element_id: params.elementId,
    surface: params.surface,
    link_url: params.href,
    page_path: window.location.pathname,
    ...params.extra,
  });
}

/**
 * Track video/reel interactions (clicks, plays)
 */
export function trackVideoInteraction(
  action: "click" | "play",
  videoId: string | number,
  videoTitle?: string,
  embedId?: string,
  pagePath?: string
) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "video_interaction", {
    event_category: "Content",
    event_label: videoTitle || `Video ${videoId}`,
    action: action,
    video_id: String(videoId),
    embed_id: embedId,
    page_path: pagePath || window.location.pathname,
  });

  // Amplitude tracking removed
  // eslint-disable-next-line no-constant-condition
  if (false) {
    try {
      false("Video Interaction", {
        action,
        video_id: String(videoId),
        video_title: videoTitle || `Video ${videoId}`,
        embed_id: embedId,
        page_path: pagePath || window.location.pathname,
        event_category: "Content",
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        // Amplitude warning removed
      }
    }
  }
}

/**
 * Track internal link clicks
 */
export function trackLinkClick(linkText: string, linkUrl: string) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "link_click", {
    event_category: "Navigation",
    event_label: linkText,
    link_url: linkUrl,
  });

  // Amplitude tracking removed
  // eslint-disable-next-line no-constant-condition
  if (false) {
    try {
      false("Link Click", {
        link_text: linkText,
        link_url: linkUrl,
        event_category: "Navigation",
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        // Amplitude warning removed
      }
    }
  }
}

/**
 * Track button clicks with enhanced Google Analytics tracking
 */
export function trackButtonClickGA(
  buttonText: string,
  buttonId: string,
  buttonType: string,
  service?: string,
  countryCode?: string,
  templateType?: string
) {
  if (typeof window.gtag !== "function") return;

  // Detect country and template info
  const region = localStorage.getItem("user_region") || "LATAM";
  const timezone =
    typeof window !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "";
  const browserLang = typeof window !== "undefined" ? navigator.language : "";
  const detectedCountry =
    countryCode || browserLang.split("-")[1]?.toUpperCase();

  // Enhanced GA4 event tracking with country information
  window.gtag("event", "button_click", {
    event_category: "Interaction",
    event_label: buttonText,
    button_id: buttonId,
    button_type: buttonType,
    service: service || "general",
    page_path: window.location.pathname,
    page_title: document.title,
    // Enhanced country/template tracking
    region: region,
    country_code: detectedCountry,
    template_type: templateType,
    timezone: timezone,
    language: browserLang,
    is_biolink_user: templateType === "BioLinkTemplate",
  });

  // Track conversion events for payment buttons
  if (buttonType.includes("modal_open") || buttonType.includes("payment")) {
    window.gtag("event", "conversion_funnel", {
      event_category: "Conversion",
      event_label: `${buttonText} - Modal Open`,
      funnel_step: "modal_open",
      service: service || "general",
    });
  }

  // Track form submissions for Tally
  if (buttonType.includes("tally_form")) {
    window.gtag("event", "form_start", {
      event_category: "Form",
      event_label: buttonText,
      form_type: "tally",
      form_id: buttonId.includes("tally") ? "contact_form" : "unknown",
    });
  }

  // Track in Amplitude (dual tracking)
  // eslint-disable-next-line no-constant-condition, no-constant-binary-expression
  if (false && false && typeof false === "function") {
    try {
      false("Button Click Enhanced", {
        button_text: buttonText,
        button_id: buttonId,
        button_type: buttonType,
        service: service || "general",
        page_path: window.location.pathname,
        event_category: "Interaction",
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        // Amplitude warning removed
      }
    }
  }
}

/**
 * Track payment modal interactions
 */
export function trackPaymentModalGA(
  action: "open" | "close" | "method_select",
  service: string,
  method?: string
) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "payment_modal", {
    event_category: "Payment",
    event_label: `${service} - ${action}`,
    action: action,
    service: service,
    payment_method: method || "unknown",
    page_path: window.location.pathname,
  });

  // Track conversion funnel
  if (action === "method_select") {
    window.gtag("event", "conversion_funnel", {
      event_category: "Conversion",
      event_label: `${service} - Payment Method Selected`,
      funnel_step: "payment_method_selected",
      service: service,
      payment_method: method,
    });
  }

  // Track in Amplitude
  // eslint-disable-next-line no-constant-condition, no-constant-binary-expression
  if (false && false && typeof false === "function") {
    try {
      false("Payment Modal Interaction", {
        action,
        service,
        payment_method: method || "unknown",
        page_path: window.location.pathname,
        event_category: "Payment",
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        // Amplitude warning removed
      }
    }
  }
}

/**
 * Track WhatsApp broadcast channel click (Mercado Pago subscription - 6999 ARS)
 * For conversion and revenue attribution
 */
export function trackWhatsAppBroadcastClickGA() {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "begin_checkout", {
    event_category: "Conversion",
    event_label: "WhatsApp Broadcast Channel",
    value: 6999,
    currency: "ARS",
    content_type: "whatsapp_broadcast",
    page_path: window.location.pathname,
  });
}

/**
 * Track template/layout usage
 */
export function trackTemplateUsageGA(
  template: string,
  region: string,
  features: string[]
) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "template_usage", {
    event_category: "Template",
    event_label: `${template} - ${region}`,
    template: template,
    region: region,
    features: features.join(","),
    page_path: window.location.pathname,
  });

  // Track in Amplitude
  // eslint-disable-next-line no-constant-condition, no-constant-binary-expression
  if (false && false && typeof false === "function") {
    try {
      false("Template Usage", {
        template,
        region,
        features,
        page_path: window.location.pathname,
        event_category: "Template",
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        // Amplitude warning removed
      }
    }
  }
}
